import { useMutation, useQuery, type WatchQueryFetchPolicy } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useUser } from '@/modules/common/application/context/user';
import { useUsers } from '@/modules/common/application/use-users';
import {
  CreateProjectMutation,
  CreateProjectMutationVariables,
  DeleteProjectMutation,
  DeleteProjectMutationVariables,
  GetProjectsQuery,
  GetProjectsQueryVariables,
  InviteProjectMembersMutation,
  InviteProjectMembersMutationVariables,
  LeaveProjectMutation,
  LeaveProjectMutationVariables,
  ProjectRole,
  RemoveProjectMemberMutation,
  RemoveProjectMemberMutationVariables,
  UpdateProjectMemberMutation,
  UpdateProjectMemberMutationVariables,
  UpdateProjectMutation,
  UpdateProjectMutationVariables,
} from '@/modules/common/types';

import {
  CREATE_PROJECT,
  DELETE_PROJECT,
  INVITE_PROJECT_MEMBERS,
  LEAVE_PROJECT,
  REMOVE_PROJECT_MEMBER,
  UPDATE_PROJECT,
  UPDATE_PROJECT_MEMBER,
} from '../api/project.mutation';
import { GET_PROJECTS } from '../api/project.query';
import { ListProjectMember } from '../types/ProjectMember';

type UseProjectsOptions = {
  fetchPolicy?: WatchQueryFetchPolicy;
  nextFetchPolicy?: WatchQueryFetchPolicy;
};

export const useProjects = ({
  fetchPolicy = 'cache-and-network',
  nextFetchPolicy = 'cache-first',
}: UseProjectsOptions = {}) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { users } = useUsers();
  const { data, loading: projectsLoading } = useQuery<GetProjectsQuery, GetProjectsQueryVariables>(
    GET_PROJECTS,
    {
      fetchPolicy,
      nextFetchPolicy,
      notifyOnNetworkStatusChange: true,
    }
  );
  const projects = useMemo(() => data?.projects || [], [data?.projects]);
  const projectsLoaded = data?.projects !== undefined;

  const [createProject, { loading: createLoading }] = useMutation<
    CreateProjectMutation,
    CreateProjectMutationVariables
  >(CREATE_PROJECT, {
    refetchQueries: [{ query: GET_PROJECTS }],
    awaitRefetchQueries: true,
    onError: () => {
      toast.error(t('modules.mavue2.project.create.failed'), {
        description: t('modules.mavue2.project.create.uniqueNameHint'),
      });
    },
  });

  const [updateProject, { loading: updateLoading }] = useMutation<
    UpdateProjectMutation,
    UpdateProjectMutationVariables
  >(UPDATE_PROJECT, {
    update: (cache, { data }) => {
      cache.modify({
        id: cache.identify({
          __typename: 'Project',
          id: data?.updateProject?.id,
        }),
        fields: {
          title: () => data?.updateProject?.title || '',
          description: () => data?.updateProject?.description || '',
        },
      });
    },
  });

  const [deleteProject, { loading: deleteLoading }] = useMutation<
    DeleteProjectMutation,
    DeleteProjectMutationVariables
  >(DELETE_PROJECT, {
    refetchQueries: [{ query: GET_PROJECTS }],
    awaitRefetchQueries: true,
    update: (cache, { data }) => {
      const deletedProjectId = data?.deleteProject?.id;
      if (deletedProjectId) {
        cache.modify({
          fields: {
            projects: (existingProjects = [], { readField }) => {
              return existingProjects.filter(
                (projectRef: any) => readField('id', projectRef) !== deletedProjectId
              );
            },
          },
        });

        cache.evict({
          id: cache.identify({
            __typename: 'Project',
            id: deletedProjectId,
          }),
        });
      }
    },
  });

  const [inviteProjectMembers, { loading: inviteProjectMembersLoading }] = useMutation<
    InviteProjectMembersMutation,
    InviteProjectMembersMutationVariables
  >(INVITE_PROJECT_MEMBERS, {
    refetchQueries: [{ query: GET_PROJECTS }],
  });

  const [updateProjectMember, { loading: updateProjectMemberLoading }] = useMutation<
    UpdateProjectMemberMutation,
    UpdateProjectMemberMutationVariables
  >(UPDATE_PROJECT_MEMBER, {
    update: (cache, { data }) => {
      if (data?.updateProjectMember) {
        const updatedMember = data.updateProjectMember;
        cache.modify({
          id: cache.identify({
            __typename: 'ProjectMember',
            id: updatedMember.id,
          }),
          fields: {
            role: () => updatedMember.role,
          },
        });
      }
    },
  });

  const [leaveProject, { loading: leaveProjectLoading }] = useMutation<
    LeaveProjectMutation,
    LeaveProjectMutationVariables
  >(LEAVE_PROJECT, {
    refetchQueries: [{ query: GET_PROJECTS }],
    awaitRefetchQueries: true,
    update: (cache, { data }) => {
      const leftProjectId = data?.leaveProject?.id;
      if (leftProjectId) {
        cache.modify({
          fields: {
            projects: (existingProjects = [], { readField }) => {
              return existingProjects.filter(
                (projectRef: any) => readField('id', projectRef) !== leftProjectId
              );
            },
          },
        });

        cache.evict({
          id: cache.identify({
            __typename: 'Project',
            id: leftProjectId,
          }),
        });
      }
    },
  });

  const [removeProjectMember, { loading: removeProjectMemberLoading }] = useMutation<
    RemoveProjectMemberMutation,
    RemoveProjectMemberMutationVariables
  >(REMOVE_PROJECT_MEMBER, {
    update: (cache, { data }, { variables }) => {
      if (data?.removeProjectMember && variables?.input?.projectId) {
        const projectId = variables.input.projectId;
        const removedMemberId = data.removeProjectMember.id;

        cache.modify({
          id: cache.identify({
            __typename: 'Project',
            id: projectId,
          }),
          fields: {
            members: (existingMembers, { readField }) => {
              return existingMembers.filter(
                (memberRef: any) => readField('id', memberRef) !== removedMemberId
              );
            },
          },
        });

        cache.evict({
          id: cache.identify({
            __typename: 'ProjectMember',
            id: removedMemberId,
          }),
        });
      }
    },
  });

  const getProjectMembers = useCallback(
    (projectId: string): ListProjectMember[] => {
      const project = projects.find((p) => p.id === projectId);
      const members =
        project?.members?.map((m) => {
          return {
            ...m,
            user: users?.find((u) => u.id === m.userId) || null,
          };
        }) || [];
      return members;
    },
    [projects, users]
  );

  const inviteMembers = async ({
    projectId,
    userIds,
    role,
  }: {
    projectId: string;
    userIds: string[];
    role: ProjectRole;
  }): Promise<void> => {
    try {
      await inviteProjectMembers({
        variables: {
          input: {
            projectId,
            userIds,
            role,
          },
        },
      });

      toast.success(
        t('modules.mavue2.project.members.invitedSuccessfully', {
          count: userIds.length,
        }),
        {
          description: t('modules.mavue2.project.members.invitationRoleDescription', {
            role: t(`modules.mavue2.project.roles.${role.toLowerCase()}.label`),
          }),
        }
      );
    } catch (err) {
      const errorMessage = t('modules.mavue2.project.members.inviteFailed');
      toast.error(t('modules.mavue2.project.members.invitationFailed'), {
        description: errorMessage,
      });
      throw new Error(errorMessage, { cause: err });
    }
  };

  const updateMemberRole = useCallback(
    async ({
      projectId,
      memberId,
      newRole,
    }: {
      projectId: string;
      memberId: string;
      newRole: ProjectRole;
    }): Promise<void> => {
      try {
        await updateProjectMember({
          variables: {
            input: {
              projectId,
              memberId,
              role: newRole,
            },
          },
        });

        toast.success(t('modules.mavue2.project.members.roleUpdated'));
      } catch (err) {
        const errorMessage = t('modules.mavue2.project.members.roleUpdateFailed');
        toast.error(t('modules.mavue2.project.members.updateFailed'), {
          description: errorMessage,
        });
        throw new Error(errorMessage, { cause: err });
      }
    },
    [t, updateProjectMember]
  );

  const removeMember = useCallback(
    async ({ projectId, memberId }: { projectId: string; memberId: string }): Promise<void> => {
      try {
        await removeProjectMember({
          variables: {
            input: {
              projectId,
              memberId,
            },
          },
        });

        toast.success(t('modules.mavue2.project.members.memberRemoved'));
      } catch (err) {
        const errorMessage = t('modules.mavue2.project.members.removeFailed');
        toast.error(t('modules.mavue2.project.members.removalFailed'), {
          description: errorMessage,
        });
        throw new Error(errorMessage, { cause: err });
      }
    },
    [removeProjectMember, t]
  );

  const leaveProjectHandler = useCallback(
    async ({ projectId }: { projectId: string }): Promise<void> => {
      try {
        const project = projects.find((p) => p.id === projectId);
        const currentUserMember = project?.members?.find((m) => m.userId === user?.id);

        if (currentUserMember?.role === ProjectRole.OWNER) {
          const otherOwnerMembers =
            project?.members
              ?.filter((m) => m.userId !== user?.id)
              .filter((m) => m.role === ProjectRole.OWNER) || [];
          if (otherOwnerMembers.length > 0) {
            await leaveProject({
              variables: {
                id: projectId,
              },
            });
            toast.success(t('modules.mavue2.project.settings.leftProject'), {
              description: t('modules.mavue2.project.settings.ownershipTransferred'),
            });
          } else {
            toast.error(t('modules.mavue2.project.settings.noOtherOwners'), {
              description: t('modules.mavue2.project.settings.onlyOwnerCannotLeave'),
            });
          }
        } else {
          await leaveProject({
            variables: {
              id: projectId,
            },
          });
          toast.success(t('modules.mavue2.project.settings.leftProject'));
        }
      } catch (err) {
        const errorMessage = t('modules.mavue2.project.settings.leaveFailed');
        toast.error(t('modules.mavue2.project.settings.leaveFailedTitle'), {
          description: errorMessage,
        });
        throw new Error(errorMessage, { cause: err });
      }
    },
    [projects, user?.id, leaveProject, t]
  );

  const getCurrentUserRole = useCallback(
    (projectId: string): ProjectRole | null => {
      const project = projects.find((p) => p.id === projectId);
      const members = project?.members || [];
      const currentUserMember = members.find((m) => m.userId === user?.id);
      return currentUserMember?.role || null;
    },
    [projects, user?.id]
  );

  const getProject = useCallback(
    (projectId: string) => {
      return projects.find((p) => p.id === projectId) || null;
    },
    [projects]
  );

  return {
    projects,
    projectsLoaded,
    projectsLoading,
    deleteLoading,
    createLoading,
    updateLoading,
    updateProjectMemberLoading,
    leaveProjectLoading,
    removeProjectMemberLoading,
    inviteProjectMembersLoading,
    createProject,
    updateProject,
    deleteProject,
    getProjectMembers,
    inviteMembers,
    updateMemberRole,
    removeMember,
    leaveProject: leaveProjectHandler,
    getCurrentUserRole,
    getProject,
  };
};
